import AppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import Menu from "@mui/material/Menu"
import Avatar from "@mui/material/Avatar"
import MenuItem from "@mui/material/MenuItem"
import { CardMedia, Backdrop, BackdropProps, IconButton } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import { PermIdentity, Logout } from "@mui/icons-material"
import OreveLogo from "assets/svg/oreve-logo.svg"
import { Link, useNavigate } from "react-router-dom"
import React, { useState } from "react"
import { useAuth } from "services/auth/auth-service-hooks"
import { useUserContext } from "context/UserContext"
import person from "assets/svg/icon-person.svg"
import { OrganizationTag } from "layouts/profile/componant/OrganizationTag.tsx"
import { useProfileContext } from "layouts/profile/context/useProfileContext.ts"
import { Organization } from "types/profile"
import { useOrganization } from "context/OrganizationContext"
import { useFeatureFlags } from "context/FeatureFlagsContext.tsx"
import Alert from "@mui/material/Alert"
import CloseIcon from "@mui/icons-material/Close"
import { Button } from "@oreve/business-ui"

const BackdropUnderHeader: React.FC<BackdropProps> = ({ onClick, ...rest }) => (
    <Backdrop
        onClick={onClick}
        sx={{
            top: "80px",
            height: "calc(100% - 80px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        {...rest}
    />
)

export const Header = () => {
    const { logout } = useAuth()
    const { first_name: userFirstName, emsp_partner_name: entity } =
        useUserContext()
    const { gdprMessageEnabled, setGdprMessageEnabled } = useFeatureFlags()
    const { setOrganizationId } = useOrganization()
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
    const [anchorElOrganization, setAnchorElOrganization] =
        React.useState<null | HTMLElement>(null)

    const navigateTo = useNavigate()
    const { multiOrganizationEnabled } = useFeatureFlags()

    const { organization, organizationLoading, organizationSuccess } =
        useProfileContext()

    const [selectedOrgId, setSelectedOrgId] = React.useState<string | null>(
        typeof window !== "undefined"
            ? sessionStorage.getItem("organizationId")
            : null
    )
    const [selectedOrgName, setSelectedOrgName] = React.useState<string | null>(
        null
    )

    const findOrganizationName = React.useCallback(
        (root: Organization | null, id: string | null): string | null => {
            if (!root || !id) return null
            const stack: Organization[] = [root]
            while (stack.length) {
                const curr = stack.pop() as Organization
                if (curr.id === id) return curr.name
                if (curr.children && curr.children.length) {
                    for (let i = 0; i < curr.children.length; i++)
                        stack.push(curr.children[i])
                }
            }
            return null
        },
        []
    )

    // Keep name in sync when organization tree or selected id changes
    React.useEffect(() => {
        setSelectedOrgName(
            findOrganizationName(
                organization as Organization | null,
                selectedOrgId
            )
        )
    }, [organization, selectedOrgId, findOrganizationName])

    // React to changes from other components/tabs
    React.useEffect(() => {
        if (typeof window === "undefined") return

        const readFromStorage = () => {
            try {
                const id = sessionStorage.getItem("organizationId")
                setSelectedOrgId(id)
            } catch {
                setSelectedOrgId(null)
            }
        }

        const sameTabHandler = (event: Event) => {
            if (event instanceof CustomEvent) {
                const detailId =
                    typeof event.detail === "string" ? event.detail : undefined
                if (detailId) {
                    setSelectedOrgId(detailId)
                    return
                }
            }
            readFromStorage()
        }

        window.addEventListener("storage", readFromStorage)
        window.addEventListener("organizationIdChanged", sameTabHandler)
        return () => {
            window.removeEventListener("storage", readFromStorage)
            window.removeEventListener("organizationIdChanged", sameTabHandler)
        }
    }, [])

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget)
        setAnchorElOrganization(null)
    }

    const handleCloseUserMenu = () => {
        setAnchorElUser(null)
    }
    const handleLogout = () => {
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        })
    }
    const goOnProfile = () => {
        setAnchorElUser(null)
        navigateTo("/user")
    }

    const handleVoirPlusClick = () => {
        window.open(
            "https://legal.oreve.com/info/changement-os-FR.pdf",
            "_blank",
            "noopener,noreferrer"
        )
    }

    const settings = [
        {
            dataCy: "header__profile--account",
            label: "Mon compte",
            icon: <PermIdentity sx={{ color: "#39C610", fontSize: "24px" }} />,
            eventClick: goOnProfile,
        },
        {
            dataCy: "header__profile--logout",
            label: "Déconnexion",
            icon: (
                <Logout
                    data-cy="logout_menu"
                    sx={{ color: "#39C610", fontSize: "24px" }}
                />
            ),
            eventClick: handleLogout,
        },
    ]

    const handleOpenOrganizationMenu = (
        event: React.MouseEvent<HTMLElement>
    ) => {
        if (!multiOrganizationEnabled) return

        // Open the organization menu only when organization data was fetched successfully
        setAnchorElOrganization(event.currentTarget)
        setAnchorElUser(null)
    }

    const handleCloseOrganizationMenu = () => {
        setAnchorElOrganization(null)
    }

    const displayName =
        organizationSuccess === true ? (selectedOrgName ?? entity) : entity

    return (
        <AppBar
            position="static"
            sx={{
                marginTop: "20px",
                display: "flex",
                position: "absolute",
                justifyContent: "center",
                boxShadow: "none",
                height: "80px",
                paddingInline: "40px",
                bgcolor:
                    anchorElUser || anchorElOrganization
                        ? "white"
                        : "transparent",
                zIndex: 1,
            }}
        >
            <Toolbar
                disableGutters
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                }}
            >
                <Link to={"/"}>
                    <CardMedia
                        component="img"
                        src={OreveLogo}
                        alt="Oreve logo"
                        sx={{ width: "139px", height: "auto" }}
                    />
                </Link>
                `
                {gdprMessageEnabled?.value && (
                    <Alert
                        sx={{
                            backgroundColor: "white",
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                            border: "none",
                            borderRadius: "16px",
                            height: "90px",
                            alignItems: "center",
                        }}
                        variant="outlined"
                        severity="info"
                        color="info"
                        action={
                            <IconButton
                                onClick={() => setGdprMessageEnabled(null)}
                                size="small"
                            >
                                <CloseIcon sx={{ color: "black" }} />
                            </IconButton>
                        }
                    >
                        <Box>
                            <div>{gdprMessageEnabled.message.fr}</div>
                            <Button
                                sx={{ textTransform: "none" }}
                                onClick={handleVoirPlusClick}
                            >
                                Voir plus
                            </Button>
                        </Box>
                    </Alert>
                )}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "4px",
                    }}
                >
                    <Box
                        onClick={
                            multiOrganizationEnabled && !organizationLoading
                                ? anchorElOrganization
                                    ? handleCloseOrganizationMenu
                                    : handleOpenOrganizationMenu
                                : undefined
                        }
                        sx={() => ({
                            display: { xs: "none", sm: "flex" },
                            borderRadius: "8px",
                            transition: "box-shadow 0.2s ease-in-out",
                            paddingInline: "8px",
                            paddingBlock: "6px",
                            flexDirection: "row",
                            gap: "10px",
                            alignItems: "center",
                            ...(organizationLoading
                                ? {
                                      pointerEvents: "none",
                                      cursor: "default",
                                      boxShadow: "none",
                                  }
                                : {
                                      "&:hover": {
                                          cursor: "pointer",
                                          boxShadow:
                                              "inset 0 0 100px 100px rgba(0, 0, 0, 0.05)",
                                      },
                                  }),
                        })}
                        data-cy="header__entity--info"
                    >
                        {organizationLoading ? (
                            <CircularProgress
                                size={20}
                                thickness={5}
                                color="primary"
                            />
                        ) : (
                            <>
                                <Avatar
                                    sx={{
                                        bgcolor: "primary.main",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "white",
                                        }}
                                        variant={"labelLarge"}
                                    >
                                        {displayName
                                            .substring(0, 3)
                                            .toUpperCase()}
                                    </Typography>
                                </Avatar>
                                <Typography
                                    color="grey.900"
                                    variant="labelLarge"
                                >
                                    {displayName}
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Box
                        data-cy="header__profile--info"
                        onClick={
                            anchorElUser
                                ? handleCloseUserMenu
                                : handleOpenUserMenu
                        }
                        sx={() => ({
                            borderRadius: "8px",
                            transition: "box-shadow 0.2s ease-in-out",
                            paddingInline: "8px",
                            paddingBlock: "6px",
                            display: "flex",
                            flexDirection: "row",
                            gap: "10px",
                            alignItems: "center",
                            "&:hover": {
                                cursor: "pointer",
                                boxShadow:
                                    "inset 0 0 100px 100px rgba(0, 0, 0, 0.05)",
                            },
                        })}
                    >
                        <Avatar alt="Profile Picture" src="#">
                            <img src={person} alt={"icon-person"} />
                        </Avatar>
                        <Typography
                            color="grey.900"
                            variant="labelLarge"
                            sx={{ display: { xs: "none", sm: "block" } }}
                        >
                            Bonjour {userFirstName}
                        </Typography>
                    </Box>

                    <Menu
                        sx={{
                            marginTop: "70px",
                            "& .MuiPaper-root": {
                                width: "auto",
                            },
                        }}
                        slots={{ backdrop: BackdropUnderHeader }}
                        id="menu-appbar"
                        anchorEl={anchorElUser}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        open={Boolean(anchorElUser)}
                        onClose={handleCloseUserMenu}
                    >
                        {settings.map((setting) => (
                            <MenuItem
                                data-cy={setting.dataCy}
                                key={setting.label}
                                onClick={setting.eventClick}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    paddingRight: "3rem",
                                    paddingLeft: "0.75rem",
                                }}
                            >
                                {setting.icon}
                                <Typography textAlign="center">
                                    {setting.label}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>
                    <Menu
                        sx={{
                            marginTop: "70px",
                            "& .MuiPaper-root": {
                                width: "auto",
                            },
                            "& .MuiMenu-list": {
                                paddingTop: 0,
                                paddingBottom: 0,
                                padding: 0,
                            },
                        }}
                        slots={{ backdrop: BackdropUnderHeader }}
                        anchorEl={anchorElOrganization}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                        open={
                            Boolean(anchorElOrganization) &&
                            multiOrganizationEnabled
                        }
                        onClose={handleCloseOrganizationMenu}
                    >
                        <Box
                            sx={{ paddingX: "0.5rem", paddingY: "0.5rem" }}
                            data-cy="header__entity--content"
                        >
                            {multiOrganizationEnabled ? (
                                <OrganizationTag
                                    CurrentOrganization={organization}
                                    mode={"list"}
                                    onClick={(id) => {
                                        try {
                                            sessionStorage.setItem(
                                                "organizationId",
                                                id
                                            )
                                            setOrganizationId(id)
                                        } catch (e) {
                                            console.error(
                                                "Failed to write organizationId in sessionStorage",
                                                e
                                            )
                                        }
                                    }}
                                />
                            ) : null}
                        </Box>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    )
}
